"use client";

import { useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { X, ArrowUpRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useEssenceDetail } from "@/hooks/use-essence-detail";
import { EssenceDetail, EssenceHeading, CopyMarkdownButton, getSource } from "./essence-detail";
import { FavoriteButton } from "./favorite-button";
import { cn } from "@/lib/utils";

interface EssenceModalProps {
  id: string;
  onClose: () => void;
}

export function EssenceModal({ id, onClose }: EssenceModalProps) {
  const searchParams = useSearchParams();
  const [version, setVersion] = useState<number | undefined>(undefined);
  const { data, isLoading, error } = useEssenceDetail(id, version);

  const handleVersionChange = (nextVersion: number) => {
    setVersion(nextVersion === data?.essence.latestVersionNumber ? undefined : nextVersion);
  };

  const browseQuery = searchParams.toString();
  const fullHref = browseQuery ? `/essences/${id}?${browseQuery}` : `/essences/${id}`;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        size="reader"
        showCloseButton={false}
        className={cn(
          "flex flex-col gap-0 overflow-hidden p-0",
          // Desktop: tall centered reader. Mobile: full-screen sheet.
          "sm:h-[92dvh] sm:max-h-[92dvh]",
          "max-sm:top-0 max-sm:left-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0",
          "max-sm:data-[state=open]:animate-slide-up max-sm:data-[state=closed]:animate-slide-down"
        )}
      >
        <DialogTitle className="sr-only">{data?.version.title ?? "Essence"}</DialogTitle>
        <DialogDescription className="sr-only">
          {data ? `By ${data.version.author}` : "Loading Essence"}
        </DialogDescription>

        {/* Sticky top bar */}
        <div className="flex shrink-0 items-start gap-3 border-b border-line bg-[rgb(23_20_42/0.85)] px-4 py-3 backdrop-blur-md sm:px-6 sm:py-4">
          <div className="min-w-0 flex-1">
            {data ? (
              <EssenceHeading version={data.version} size="modal" as="span" />
            ) : (
              <div className="space-y-2.5 py-1">
                <Skeleton className="h-2.5 w-24 rounded-full" />
                <Skeleton className="h-6 w-2/3 rounded-md" />
                <Skeleton className="h-3 w-32 rounded-full" />
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {data && (
              <>
                <FavoriteButton essenceId={data.essence.id} size="md" />
                <CopyMarkdownButton
                  version={data.version}
                  source={getSource(data.version)}
                  size="icon-sm"
                  className="hidden sm:inline-flex"
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon-sm" asChild aria-label="Open full page">
                      <Link href={fullHref}>
                        <ArrowUpRight />
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Open full page</TooltipContent>
                </Tooltip>
              </>
            )}
            <DialogClose asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Close">
                <X />
              </Button>
            </DialogClose>
          </div>
        </div>

        {/* Scrolling body */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-8 sm:py-7">
          {isLoading ? (
            <div className="mx-auto w-full max-w-[72ch] space-y-6" aria-busy>
              <Skeleton className="h-3 w-40 rounded-full" />
              <Skeleton className="h-28 rounded-xl" />
              <div className="space-y-2.5">
                {Array.from({ length: 7 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    className="h-3.5 rounded-full"
                    style={{ width: `${95 - (i % 4) * 9}%` }}
                  />
                ))}
              </div>
            </div>
          ) : error || !data ? (
            <div className="mx-auto max-w-md py-16 text-center">
              <p className="font-display text-[17px] font-semibold text-ink">
                This Essence could not be loaded
              </p>
              <p className="mt-1.5 text-[13.5px] text-ink-3">
                Check your connection and try again.
              </p>
            </div>
          ) : (
            <EssenceDetail
              data={data}
              onVersionChange={handleVersionChange}
              browseQuery={browseQuery}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
